import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import { subscribeToPushNotifications } from '../utils/pushNotificationUtils';

const PushNotificationModal = () => {
  const [showModal, setShowModal] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    // Check if user has already subscribed or denied
    const isSubscribed = localStorage.getItem('pushSubscribed');
    const hasDenied = localStorage.getItem('pushDenied');
    
    // Show modal after 2 seconds if not subscribed and not denied
    if (!isSubscribed && !hasDenied) {
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAllow = async () => {
    setIsSubscribing(true);
    try {
      const success = await subscribeToPushNotifications();
      if (success) {
        setShowModal(false);
      } else {
        alert('Please enable notifications in your browser settings to receive exclusive offers and updates.');
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      alert('Failed to enable notifications. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleNotNow = () => {
    setShowModal(false);
    // Don't mark as denied, will show again on next visit
  };

  const handleNeverAsk = () => {
    setShowModal(false);
    localStorage.setItem('pushDenied', 'true');
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md mx-4 overflow-hidden animate-scale-in">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8 text-center">
          <div className="bg-white/20 rounded-full p-4 inline-flex mb-4 animate-bounce">
            <Bell className="h-12 w-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Stay Connected!
          </h2>
          <p className="text-blue-100 text-sm">
            Get instant updates on exclusive offers and important announcements
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-2 mt-1">
                <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Exclusive Deals</h3>
                <p className="text-sm text-gray-600">Be the first to know about special offers</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="bg-blue-100 rounded-full p-2 mt-1">
                <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Service Updates</h3>
                <p className="text-sm text-gray-600">Get notified about maintenance and updates</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="bg-purple-100 rounded-full p-2 mt-1">
                <svg className="h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Never Miss Out</h3>
                <p className="text-sm text-gray-600">Receive time-sensitive information instantly</p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-2">
            <Button
              onClick={handleAllow}
              disabled={isSubscribing}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 text-base"
            >
              {isSubscribing ? 'Enabling...' : '🔔 Enable Notifications'}
            </Button>
            
            <div className="flex gap-2">
              <Button
                onClick={handleNotNow}
                variant="outline"
                className="flex-1 text-sm"
              >
                Not Now
              </Button>
              <Button
                onClick={handleNeverAsk}
                variant="ghost"
                className="flex-1 text-sm text-gray-500"
              >
                Never Ask Again
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            You can change your notification preferences anytime in your browser settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationModal;
